import { Form, FormInstance } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { useEffect, useState } from "react";

type NamePath = string | number | (string | number)[];

type UseFormSubmitDisabledParams<T extends object> = {
  form: FormInstance<T>;
  loading?: boolean;
  initialValues?: Partial<T>;
  requireChanges?: boolean;
  requiredFields?: NamePath[];
  extraDisabled?: boolean;
};

const isEmptyValue = (value: unknown) => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim().length === 0;

  return value === undefined || value === null || value === false;
};

export const useFormSubmitDisabled = <T extends object>({
  form,
  loading = false,
  requiredFields = [],
  extraDisabled = false,
}: UseFormSubmitDisabledParams<T>) => {
  const watchedValues = Form.useWatch([], form);
  const [validationTick, setValidationTick] = useState(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setValidationTick((value) => value + 1);
    }, FORM_VALIDATION_DEBOUNCE + 50);

    return () => clearTimeout(timeoutId);
  }, [watchedValues]);

  void validationTick;

  const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
  const hasMissingRequiredFields = requiredFields.some((field) => isEmptyValue(form.getFieldValue(field as any)));

  return loading || extraDisabled || hasErrors || hasMissingRequiredFields;
};
